<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import ExpandableCard from '~/components/shared/ExpandableCard.vue';
import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { formatCurrencyForDisplay } from '~/utils/formatters';

import DocumentsTable from '../components/DocumentsTable.vue';
import FinancingDataCard from '../components/FinancingDataCard.vue';
import FinancingDataTable from '../components/FinancingDataTable.vue';
import OperationsHistoryTable from '../components/OperationsHistoryTable.vue';
import useDocumentActions from '../hooks/useDocumentActions';
import useDateFormat from '../hooks/useDateFormat';
import useRouteParams from '../hooks/useRouteParams';
import useFinancingDetail from '../queries/useFinancingDetail';
import type { FinancingData } from '../types/financingDetail.types';

const route = useRoute('/cessions.[cessionId].financings.[financingId]');
const { cessionId, financingId } = useRouteParams(route);

const {
  data: financingDetail,
  error,
  isError,
  isLoading,
} = useFinancingDetail(financingId);

const { formatDate } = useDateFormat();

const breadcrumbItems = computed(() => [
  { label: 'Mercado de Capitais', to: '/cessions' },
  { label: `Detalhes da Cessão #${cessionId.value}`, to: `/cessions/${cessionId.value}` },
  { label: `Detalhes do Financiamento #${financingId.value}` },
]);

const DOCUMENT_TYPE_CONTRATO_ASSINADO = 'contrato_assinado';

const ccbDocument = computed<FinancingData | null>(() => {
  if (!financingDetail.value?.documents) return null;
  return financingDetail.value.documents.find(
    (doc: FinancingData) => doc.file_type === DOCUMENT_TYPE_CONTRATO_ASSINADO,
  ) || null;
});

const {
  downloadDocument: handleDownloadCCB,
  isDownloading,
  isViewing,
  viewDocument: handleViewCCB,
} = useDocumentActions({
  document: ccbDocument,
  fileNameFallback: computed(() => financingDetail.value?.ccb || financingId.value),
  fileNamePrefix: 'CCB',
  financingId: financingId.value,
});

const financingData = computed(() => financingDetail.value?.financing_data);

const pageTitle = computed(() => `Detalhes do Financiamento #${financingId.value}`);
</script>

<template>
  <DashboardLayout
    :title="pageTitle"
    :description="`Detalhes do financiamento com cessão`"
  >
    <ol class="breadcrumb breadcrumb-arrows mb-2" aria-label="breadcrumbs">
      <li
        v-for="(item, index) in breadcrumbItems"
        :key="index"
        class="breadcrumb-item"
        :class="{ active: index === breadcrumbItems.length - 1 }"
        :aria-current="index === breadcrumbItems.length - 1 ? 'page' : undefined"
      >
        <router-link v-if="item.to" :to="item.to">{{ item.label }}</router-link>
        <span v-else>{{ item.label }}</span>
      </li>
    </ol>

    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div v-else-if="isError" class="alert alert-danger" role="alert">
      <strong>Erro ao carregar dados:</strong> {{ error?.message || 'Erro desconhecido' }}
    </div>

    <div v-else-if="financingDetail && financingData" class="row g-3">
      <div class="col-12">
        <ExpandableCard
          key="financing-data"
          title="Dados do Financiamento"
          :default-expanded="true"
        >
          <div class="row mb-0 mb-md-3">
            <FinancingDataCard
              label="Valor Bruto"
              :value="formatCurrencyForDisplay(financingData.gross_amount)"
              value-class="text-success"
            />
            <FinancingDataCard
              label="Entrada"
              :value="formatCurrencyForDisplay(financingData.down_payment)"
              value-class="text-success"
            />
            <FinancingDataCard
              label="Valor Financiado"
              :value="formatCurrencyForDisplay(financingData.net_amount)"
              value-class="text-primary"
            />
            <FinancingDataCard
              label="Parcelas"
              :value="financingData.installments_number"
              value-class="text-warning"
            />
          </div>

          <FinancingDataTable
            :banking="financingData.banking"
            :ccb="financingDetail.ccb"
            :customer-name="financingData.customer.name"
            :formalization-date="formatDate(financingData.formalization_date)"
            :grace-period="financingData.grace_period"
          />
        </ExpandableCard>
      </div>

      <div class="col-12">
        <ExpandableCard
          key="operations-history"
          title="Historico de cessões e tombamentos"
          :default-expanded="true"
        >
          <OperationsHistoryTable
            :operations="financingDetail.operations_history"
            :format-date="formatDate"
          />
        </ExpandableCard>
      </div>

      <div v-if="ccbDocument" class="col-12">
        <ExpandableCard
          key="documents"
          title="Documentos"
          :default-expanded="true"
        >
          <DocumentsTable
            document-description="Contrato de Cédula de Crédito Bancário"
            document-name="CCB"
            :is-downloading="isDownloading"
            :is-viewing="isViewing"
            :on-download="handleDownloadCCB"
            :on-view="handleViewCCB"
          />
        </ExpandableCard>
      </div>
    </div>
  </DashboardLayout>
</template>
